import { z } from "zod";
import { JobIdSchema } from "./ids";

/** Every timestamp on the wire is ISO 8601 with an offset (Postgres timestamptz serialised). */
export const IsoDateTimeSchema = z.iso.datetime({ offset: true });
export type IsoDateTime = z.infer<typeof IsoDateTimeSchema>;

/** Money in integer cents; the client formats it per Interface Language (plan: USD via Intl). */
export const MoneySchema = z.strictObject({
  cents: z.int().nonnegative(),
  currency: z.literal("USD"),
});
export type Money = z.infer<typeof MoneySchema>;

/**
 * Why a job (parse, proposal, style sheet, refine, render, assemble, sample)
 * failed, as the Author sees it. `key` is an i18n key under errors:jobs.*
 * chosen by the handler package; `detail` is a short machine detail (error
 * class, HTTP status) and never a provider body.
 */
export const FailureSchema = z.strictObject({
  key: z.string().regex(/^jobs\.[a-zA-Z0-9_.]+$/),
  detail: z.string().max(200).nullable(),
  at: IsoDateTimeSchema,
});
export type Failure = z.infer<typeof FailureSchema>;

export const ProgressSchema = z.strictObject({
  done: z.int().nonnegative(),
  total: z.int().nonnegative(),
  /** Free-form short label the handler commits with progress (e.g. a chunk index); not translated. */
  label: z.string().max(100).nullable(),
});
export type Progress = z.infer<typeof ProgressSchema>;

/** A lock on a Chapter's script while its render runs. */
export const ChapterLockSchema = z.strictObject({
  reason: z.literal("rendering"),
  jobId: JobIdSchema,
  since: IsoDateTimeSchema,
});
export type ChapterLock = z.infer<typeof ChapterLockSchema>;

/** Chapter Audio as the rail, Review, and the Estimate see it. */
export const ChapterAudioSummarySchema = z.strictObject({
  durationMs: z.int().nonnegative(),
  renderedAt: IsoDateTimeSchema,
  /** The script changed after this audio was rendered; still downloadable until a re-render replaces it. */
  stale: z.boolean(),
});
export type ChapterAudioSummary = z.infer<typeof ChapterAudioSummarySchema>;
